import React, { useState } from 'react';
import { Typography } from "@material-tailwind/react";
import { XCircleIcon, ArrowUpTrayIcon, DocumentIcon, TrashIcon } from "@heroicons/react/24/outline";

// Oculta la carga del documento por ahora; se reactivará cuando el backend exponga
// el endpoint para almacenar/asociar el documento a la iglesia.
const MOSTRAR_CARGA_DOCUMENTO = false;

export const TabFicheroCulto = ({ values, handleChange, setFieldValue, esVisualizacion }) => {
    // Documento del certificado. Por ahora solo se maneja la selección en UI;
    // la persistencia se conectará cuando el backend exponga el endpoint correspondiente.
    const [documento, setDocumento] = useState(null);

    const handleSeleccionarDocumento = (e) => {
        const archivo = e.target.files && e.target.files[0];
        if (archivo) {
            setDocumento(archivo);
        }
    };

    const quitarDocumento = () => setDocumento(null);

    return (
        <div className="bg-white p-5 rounded-md">
            <Typography variant="h6" color="blue-gray" className="font-medium mb-4">
                Certificado de Local Filial
            </Typography>

            {/* Carga del documento del certificado (oculto por ahora, pendiente de backend) */}
            {MOSTRAR_CARGA_DOCUMENTO && (
            <div className="bg-gray-100 p-4 rounded border border-gray-200 mb-6">
                <Typography variant="small" className="font-semibold text-gray-700 mb-1 uppercase">
                    Documento del certificado
                </Typography>
                <Typography variant="small" color="blue-gray" className="mb-3">
                    Cargue el documento (PDF o imagen) del Certificado de Local Filial de la iglesia.
                </Typography>

                {!documento && (
                    <label
                        htmlFor="certificado-local-filial-file"
                        className={`flex flex-col items-center justify-center gap-2 border-2 border-dashed border-gray-300 rounded-md py-6 px-4 text-center bg-white transition-colors ${esVisualizacion ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer hover:border-blue-400 hover:bg-blue-50/40'}`}
                    >
                        <ArrowUpTrayIcon className="h-7 w-7 text-blue-500" />
                        <Typography variant="small" color="blue-gray" className="font-medium">
                            Seleccionar documento
                        </Typography>
                        <Typography variant="small" className="text-gray-500">
                            PDF, JPG o PNG
                        </Typography>
                        <input
                            id="certificado-local-filial-file"
                            type="file"
                            accept=".pdf,.jpg,.jpeg,.png"
                            className="hidden"
                            onChange={handleSeleccionarDocumento}
                            disabled={esVisualizacion}
                        />
                    </label>
                )}

                {documento && (
                    <div className="flex items-center justify-between gap-3 bg-white border border-gray-200 rounded-md px-3 py-2">
                        <div className="flex items-center gap-2 min-w-0">
                            <DocumentIcon className="h-6 w-6 text-blue-500 shrink-0" />
                            <span className="text-sm text-gray-700 truncate">{documento.name}</span>
                        </div>
                        {!esVisualizacion && (
                            <button
                                type="button"
                                onClick={quitarDocumento}
                                className="text-red-500 hover:text-red-700 shrink-0"
                                title="Quitar documento"
                            >
                                <TrashIcon className="h-5 w-5" />
                            </button>
                        )}
                    </div>
                )}
            </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Propiedad */}
                <div className="bg-gray-100 p-4 rounded border border-gray-200">
                    <Typography variant="small" className="font-semibold text-gray-700 mb-4 uppercase">
                        Propiedad
                    </Typography>

                    <div className="mb-4">
                        <Typography variant="small" color="blue-gray" className="mb-1 font-medium">
                            Tipo propiedad
                        </Typography>
                        <div className="flex gap-2 items-center">
                            <select
                                className="iglesia-form-control"
                                name="id_tipo_propiedad"
                                value={values.id_tipo_propiedad || ''}
                                onChange={handleChange}
                                disabled={esVisualizacion}
                            >
                                <option value="">No asignado</option>
                                <option value="1">Local Propio</option>
                                <option value="2">Alquilado</option>
                                <option value="3">Comodato</option>
                                <option value="4">Otro</option>
                            </select>
                            {!esVisualizacion && (
                                <button
                                    type="button"
                                    onClick={() => setFieldValue('id_tipo_propiedad', '')}
                                    className="bg-red-400 text-white rounded hover:bg-red-500 h-[42px] w-[42px] flex items-center justify-center"
                                    title="Desasignar"
                                >
                                    <XCircleIcon className="h-6 w-6" />
                                </button>
                            )}
                        </div>
                    </div>

                    <div className="mb-4 flex items-start gap-2">
                        <input
                            type="checkbox"
                            className="mt-1 w-4 h-4 text-blue-600 border border-gray-300 rounded focus:ring-blue-500"
                            name="dom_fc_no_coincidente"
                            id="dom_fc_no_coincidente"
                            checked={values.dom_fc_no_coincidente}
                            onChange={handleChange}
                            disabled={esVisualizacion}
                        />
                        <label htmlFor="dom_fc_no_coincidente">
                            <Typography variant="small" color="blue-gray" className="font-medium cursor-pointer">
                                La dirección de la propiedad NO coincide con la dirección de contacto
                            </Typography>
                        </label>
                    </div>

                    <div className="mb-2">
                        <Typography variant="small" color="blue-gray" className="mb-1 font-medium">
                            Dirección de propiedad
                        </Typography>
                        <textarea
                            className="w-full text-sm px-3 py-2 border border-gray-300 rounded-md outline-none bg-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all box-border min-h-[80px]"
                            name="domicilio_fc"
                            value={values.domicilio_fc}
                            onChange={handleChange}
                            disabled={esVisualizacion}
                        />
                    </div>
                </div>

                {/* Propietario + Alquiler */}
                <div className="flex flex-col gap-6">
                    {/* Propietario */}
                    <div className="bg-gray-100 p-4 rounded border border-gray-200">
                        <Typography variant="small" className="font-semibold text-gray-700 mb-4 uppercase">
                            Propietario
                        </Typography>
                        <div className="mb-4">
                            <Typography variant="small" color="blue-gray" className="mb-1 font-medium">
                                Datos catastrales
                            </Typography>
                            <input
                                type="text"
                                className="iglesia-form-control"
                                name="datos_catastrales"
                                value={values.datos_catastrales}
                                onChange={handleChange}
                                disabled={esVisualizacion}
                            />
                        </div>
                        <div className="mb-2">
                            <Typography variant="small" color="blue-gray" className="mb-1 font-medium">
                                Partida
                            </Typography>
                            <input
                                type="text"
                                className="iglesia-form-control"
                                name="partida"
                                value={values.partida}
                                onChange={handleChange}
                                disabled={esVisualizacion}
                            />
                        </div>
                    </div>

                    {/* Alquiler */}
                    <div className="bg-gray-100 p-4 rounded border border-gray-200">
                        <Typography variant="small" className="font-semibold text-gray-700 mb-4 uppercase">
                            Alquiler
                        </Typography>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Typography variant="small" color="blue-gray" className="mb-1 font-medium">
                                    Fecha de inicio de contrato
                                </Typography>
                                <input
                                    type="date"
                                    className="iglesia-form-control"
                                    name="f_inicio_contrato"
                                    value={values.f_inicio_contrato ? values.f_inicio_contrato.substring(0, 10) : ''}
                                    onChange={handleChange}
                                    disabled={esVisualizacion}
                                />
                            </div>
                            <div>
                                <Typography variant="small" color="blue-gray" className="mb-1 font-medium">
                                    Fecha de vencimiento de contrato
                                </Typography>
                                <input
                                    type="date"
                                    className="iglesia-form-control"
                                    name="f_fin_contrato"
                                    value={values.f_fin_contrato ? values.f_fin_contrato.substring(0, 10) : ''}
                                    onChange={handleChange}
                                    disabled={esVisualizacion}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
